import React, { useContext, useEffect, useState } from 'react';  
import Question from '../components/Question';  
import '../styles/Quiz.css';  
import ResultContext from '../context/ResultContext';  
import { useNavigate, useParams } from 'react-router-dom';  
import Spinner from '../components/Spinner';  
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { premiumQuizAPI, batchAPI } from '../services/api';

export default function PremiumQuiz() {  
  const formatDate = (timestamp) => {  
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];  
    const date = new Date(timestamp);  
    const day = date.getDate();  
    const month = months[date.getMonth()];  
    const year = date.getFullYear();  
    return `${day} ${month} ${year}`;  
  };  

  const { quizId, batchId } = useParams();  
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { quizData, setQuizData, score, setScore, responses, setResponses } = useContext(ResultContext);  
  
  const [batch, setBatch] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [error, setError] = useState(null);
  const [localResponse, setLocalResponse] = useState([]);
  const [submitting, setSubmitting] = useState(false); // ✅ Added submitting state

  // Check if user has access to this batch
  const checkAccess = async () => {
    if (!isAuthenticated) {
      setCheckingAccess(false);
      setHasAccess(false);
      setLoading(false);
      return;
    }

    try {
      setCheckingAccess(true);
      const response = await batchAPI.checkBatchAccess(batchId);
      if (response.data.success) {
        setHasAccess(response.data.hasAccess || response.data.isAdmin);
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
      setError('Failed to verify access');
    } finally {
      setCheckingAccess(false);
    }
  };

  // Fetch batch details
  const fetchBatchData = async () => {
    try {
      const response = await batchAPI.getBatchDetails(batchId);
      if (response.data.success) {
        setBatch(response.data.batch);
      }
    } catch (error) {
      console.error('Error fetching batch data:', error);
      setError('Failed to load batch details');
    }
  };

  // Fetch quiz data
  const fetchQuizData = async () => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await premiumQuizAPI.getQuiz(batchId, quizId);
      if (response.data.success) {
        setQuizData(response.data.quiz);
        setLocalResponse(Array(response.data.quiz.questions.length).fill("0"));
      }
    } catch (error) {
      console.error('Error fetching quiz:', error);
      if (error.response?.data?.needsEnrollment) {
        setError('You need to enroll in this batch to access this quiz');
        setHasAccess(false);
      } else {
        setError('Failed to load quiz');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchData();
    checkAccess();
    setStartTime(Date.now());
  }, [batchId, quizId, isAuthenticated]);

  useEffect(() => {
    if (!checkingAccess && hasAccess) {
      fetchQuizData();
    }
  }, [hasAccess, checkingAccess]);

  const handleAnswerSelected = (option, id) => {
    let copyRes = [...localResponse];
    copyRes[id] = option;
    setLocalResponse(copyRes);
  };

  // ✅ UPDATED SUBMIT FUNCTION
  const onSubmit = async () => {
    // Validation checks
    if (!user) {
      alert('Please login to submit quiz');
      return;
    }

    // Check if all questions are answered
    const unansweredCount = localResponse.filter(ans => ans === "0").length;
    if (unansweredCount > 0) {
      const confirmSubmit = window.confirm(
        `You have ${unansweredCount} unanswered question(s). Do you want to submit anyway?`
      );
      if (!confirmSubmit) return;
    }

    setSubmitting(true); // ✅ Start submitting

    try {
      let tempScore = 0;
      
      // Calculate score
      for (let i = 0; i < localResponse.length; i++) {
        if (localResponse[i] === quizData.questions[i].answer) {
          tempScore++;
        }
      }

      setScore(tempScore);
      setResponses(localResponse);

      const endTime = Date.now();
      const timeTaken = Math.round((endTime - startTime) / 1000);

      // ✅ SUBMIT QUIZ API CALL
      const response = await premiumQuizAPI.submitQuiz({
        userId: user.id,
        quizId: quizData._id,
        batchId: batchId,
        score: tempScore * 2, // Assuming 2 marks per question
        totalQuestions: quizData.questions.length,
        correctAnswers: tempScore,
        timeTaken: timeTaken
      });

      if (response.data.success) {
        // console.log('✅ Quiz submitted successfully:', response.data);
        
        // ✅ Store attempt data in context if needed
        if (response.data.attempt) {
          // You can store this in context or pass via navigation state
          // console.log('Attempt Number:', response.data.attempt.attemptNumber);
          // console.log('Is First Attempt:', response.data.isFirstAttempt);
          if (response.data.ranking) {
            // console.log('User Rank:', response.data.ranking);
          }
        }

        // Navigate to result page
        navigate('/premium-result', {
          state: {
            attemptData: response.data.attempt,
            isFirstAttempt: response.data.isFirstAttempt,
            ranking: response.data.ranking,
            batchId: batchId
          }
        });
      }
    } catch (error) {
      console.error('❌ Error submitting quiz:', error);
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        alert('Quiz or user not found. Please try again.');
      } else if (error.response?.data?.needsEnrollment) {
        alert('You need to be enrolled in this batch to submit the quiz');
        navigate(`/premium-batch/${batchId}`);
      } else if (error.response?.status === 400) {
        alert(error.response.data.message || 'Invalid submission data');
      } else {
        alert('Failed to submit quiz. Please try again.');
      }
    } finally {
      setSubmitting(false); // ✅ Stop submitting
    }
  };

  // Show loading while checking access
  if (checkingAccess || !batch) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <Spinner />
          <p>Verifying access...</p>
        </div>
      </>
    );
  }

  // Show login required
  if (!isAuthenticated) {
    return (
      <>
        <Navbar />
        <div className="container access-denied-container">
          <div className="access-denied-card">
            <div className="access-denied-icon">🔒</div>
            <h2>Login Required</h2>
            <p>Please login to access this quiz</p>
            <button 
              onClick={() => navigate('/login')}
              className="btn btn-primary btn-lg"
            >
              Login with Google
            </button>
          </div>
        </div>
      </>
    );
  }

  // Show enrollment required
  if (!hasAccess && user?.role !== 'admin') {
    return (
      <>
        <Navbar />
        <div className="container access-denied-container">
          <div className="access-denied-card">
            <div className="access-denied-icon">⛔</div>
            <h2>Enrollment Required</h2>
            <p>You need to enroll in <strong>{batch.name}</strong> to access this quiz</p>
            <div className="batch-info-preview">
              <h4>{batch.name}</h4>
              <p>📚 {batch.subject}</p>
              {batch.price > 0 && <p className="price">₹{batch.price}</p>}
            </div>
            <button 
              onClick={() => navigate(`/premium-batch/${batchId}`)}
              className="btn btn-success btn-lg"
            >
              📱 Enroll Now
            </button>
            <button 
              onClick={() => navigate(`/premium-tests/${batchId}`)}
              className="btn btn-outline-secondary btn-lg mt-3"
            >
              ← Back to Quizzes
            </button>
          </div>
        </div>
      </>
    );
  }

  // Show error
  if (error) {
    return (
      <>
        <Navbar />
        <div className="container access-denied-container">
          <div className="access-denied-card">
            <div className="access-denied-icon">❌</div>
            <h2>Error</h2>
            <p>{error}</p>
            <button 
              onClick={() => navigate(`/premium-tests/${batchId}`)}
              className="btn btn-primary btn-lg"
            >
              ← Back to Quizzes
            </button>
          </div>
        </div>
      </>
    );
  }

  // Show loading quiz
  if (loading || !quizData) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <Spinner />
          <p>Loading quiz...</p>
        </div>
      </>
    );
  }

  // Show quiz
  return (
    <>
      <Navbar />
      <div className="quiz-header-section">
        <div className="container">
          <button 
            onClick={() => navigate(`/premium-tests/${batchId}`)}
            className="btn btn-outline-primary mb-3"
          >
            ← Back to Quizzes
          </button>
          <h1>📝 Daily Quiz: {formatDate(quizData.date)}</h1>
          <h3>Subject: {quizData.subject}</h3>
          <div className="quiz-info">
            <span className="quiz-info-item">
              📊 Total Questions: {quizData.questions.length}
            </span>
            <span className="quiz-info-item">
              ⏱️ Started: {new Date(startTime).toLocaleTimeString()}
            </span>
            {/* ✅ Show answered count */}
            <span className="quiz-info-item">
              ✅ Answered: {localResponse.filter(ans => ans !== "0").length}/{quizData.questions.length}
            </span>
            {user?.role === 'admin' && (
              <span className="admin-badge">👑 Admin</span>
            )}
          </div>
        </div>
      </div>

      <div className="quiz-container">
        {quizData.questions.map((question, id) => (
          <React.Fragment key={id}>
            <Question 
              question={question} 
              onAnswerSelected={handleAnswerSelected} 
              id={id} 
            />
          </React.Fragment>
        ))}

        <div className="center-container">
          <button 
            className='btn btn-primary btn-lg submit-quiz-btn' 
            onClick={onSubmit}
            disabled={submitting} // ✅ Disable while submitting
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : (
              '✅ Submit Responses'
            )}
          </button>
        </div>
      </div>
    </>
  );
}